// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { ShoppingListItem } from "@bitebalance/contracts"
import { buyAmountText, localNum, pantryCovered, shopRowName, type TFn } from "./buyLine"
import { groupBySection } from "./sections"
import { unitLabel } from "@/lib/units"

/**
 * Builds a plain-text, grouped shopping list for copy/export handoff
 * (T13). Deliberately text-only, no retailer basket API — never claims a
 * cart was transferred (AGENTS.md / ui-ux-spec.md "Pantry and shopping").
 *
 * Shop list clarity (2026-10-06): laid out like the page — rows under their supermarket section
 * (headings only once the catalog has curated at least one; before that the list exports flat, in
 * the order the page shows), each line "name: what to buy" in the words of the row ("Zwiebel: 1 Netz
 * (9 Stück)"). The old line "(1 x 9count)" showed a raw internal unit. A row the kitchen already covers says so.
 * Mirrors mobile/src/lib/shopExport.ts.
 */
export function buildShoppingListText(
  title: string,
  items: ShoppingListItem[],
  retailerLine: string,
  t: TFn,
  locale: string
): string {
  // the English list capitalises a calculated row's name; manual rows and German names are printed as served
  const lineOf = (item: ShoppingListItem) => {
    const name = shopRowName(item, locale)
    if (item.manual) {
      const q = item.requiredQuantity
      return q ? `- ${name}: ${localNum(q.display.quantity, t)} ${unitLabel(q.display.unit, t)}` : `- ${name}`
    }
    if (pantryCovered(item)) return `- ${name}: ${t("views.shop.pantryCovers")}`
    const buy = buyAmountText(item, t)
    return buy ? `- ${name}: ${buy}` : `- ${name}`
  }
  // Blocks separated by exactly one blank line: title · the rows (under a section heading once the catalog has curated one)
  // · the retailer line. An empty block is never emitted, so no case prints a doubled blank line.
  const grouped = groupBySection(items, locale)
  const blocks: string[][] = [[title]]
  for (const group of grouped.groups) {
    const rows = group.rows.map(lineOf)
    blocks.push(grouped.showHeaders ? [t(`views.shop.section.${group.section}`), ...rows] : rows)
  }
  blocks.push([retailerLine])
  return blocks.map(block => block.join("\n")).join("\n\n")
}
